import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; // if using react-router
import { useAuth } from '../../components/Providers/Auth/AuthContext';
import { login } from '../../store/reducers/userReducer';
import { useDispatch } from 'react-redux';
import axiosInstance from '../../services/apiService';
import Cookies from 'js-cookie';

const Login: React.FC = () => {
  const { setToken } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate(); // for navigation after login
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(''); // Clear previous error on new attempt
    setLoading(true);

    try {
      const response = await axiosInstance.post('/login', {
        email,
        password,
      });

      const { token, user } = response.data;

      Cookies.set('token', token);

      // Save token to useAuth
      setToken(token);

      dispatch(login({ ...response.data.user, token: response.data.token }));

      // Normalize role (some APIs may use user_type.code instead of user.role)
      const role = user.role || user.user_type?.code;

      // Redirect based on role
      switch (role) {
        case 'super_admin':
          navigate('/ShiftLogbook');
          break;

        case 'shift_supervisor':
          navigate('/ShiftLogbook');
          break;

        case 'shift_assistant':
        case 'shift_trainee':
          navigate('/ShiftLogbook');
          break;

        case 'responsible_person':
          navigate('/HVworkPermits');
          break;

        case 'appointed_operator':
          navigate('/HVworkPermits');
          break;

        case 'hot_work_monitor':
          navigate('/ShiftLogbook');
          break;

        case 'system_engineer':
          navigate('/TemporaryOperatingInstruction');
          break;

        case 'section_head':
          navigate('/TemporaryOperatingInstruction');
          break;

        case 'technical_manager':
          navigate('/TemporaryOperatingInstruction');
          break;

        case 'operation_manager':
          navigate('/TemporaryOperatingInstruction');
          break;

        case 'planner':
          navigate('/OutageRequisition');
          break;

        case 'technical_support_officer':
          navigate('/OperatingInstruction');
          break;

        case 'requestor':
          navigate('/OutageRequisition');
          break;

        case 'maintenance_manager':
          navigate('/OutageRequisition');
          break;

        case 'responsible_person_hwa':
          navigate('/HotWorkApproval');
          break;

        case 'appointed_person_lv':
          navigate('/PlantSafetyRegulation');
          break;

        default:
          console.warn('Unknown role:', role);
          navigate('/login'); // fallback
      }
    } catch (error: any) {
      console.error('Login failed:', error);

      const message = error?.response?.data?.message || 'Invalid credentials. Please try again.';

      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden fixed top-0 left-0">
      {/* Left Image Section */}
      <div className="hidden md:flex md:w-1/2 relative">
        <img src="/images/shiftLogFront.png" alt="Login" className="absolute inset-0 w-full h-full object-cover" />
      </div>

      {/* Right Form Section */}
      <div className="flex flex-col justify-center px-10 w-full md:w-1/2 bg-white">
        <div className="flex flex-col items-center mb-6">
          <img src="/images/shiftLogLogo.jpg" alt="Logo" className="h-20 mb-4" />
        </div>

        <form className="space-y-6" onSubmit={handleSubmit} autoComplete="off">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              User Name
            </label>

            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter Your Email"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>

            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter Your Password"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            />
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="flex items-center gap-2 border border-red-400 bg-white text-red-600 px-4 py-3 rounded-md">
              <span className="text-orange-500 text-lg">⚠</span>
              <span className="font-medium">{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 rounded-md text-white transition flex items-center justify-center gap-2 ${
              loading ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
